import { useEffect, useState } from "react";
import {
  Tab,
  Tabs,
  Card,
  Table,
  Stack,
  Divider,
  TableBody,
  TableContainer,
  TableRow,
  TableCell,
} from "@mui/material";
// @mui
import { useTheme } from "@mui/material/styles";
// utils
import { fTimestamp } from "../../../../utils/formatTime";
// components
import Label from "../../../../components/label";
import Scrollbar from "../../../../components/scrollbar";
import {
  useTable,
  getComparator,
  emptyRows,
  TableNoData,
  TableEmptyRows,
  TableHeadCustom,
  TablePaginationCustom,
} from "../../../../components/table";
// sections
import InvoiceAnalytic from "../../../../sections/@dashboard/invoice/InvoiceAnalytic";
import { dbInvoice } from "../../../../api/parnerus/types/invoice";
import axiosInstance from "../../../../utils/axios";
import { format } from "date-fns";
import InvoiceTableToolbar from "./InvoiceTableToolbar";
import InvoiceTableRow from "./InvoiceTableRow";
import { useAuthContext } from "../../../../auth/useAuthContext";
import axios from "../../../../utils/axios";
import { useSharedContext } from "../../../../shared/SharedComponentsContext";
import useResponsive from "../../../../hooks/useResponsive";
import InvoiceCard from "./InvoiceCard";

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: "inoiceNumber", label: "# Factura", align: "left" },
  { id: "client", label: "Cliente", align: "left" },
  { id: "createDate", label: "Creado", align: "left" },
  { id: "dueDate", label: "Vencimiento", align: "left" },
  { id: "price", label: "Total", align: "center", width: 140 },
  { id: "status", label: "Estado", align: "left" },
  { id: "" },
];

// ----------------------------------------------------------------------

export enum InvoicesTableVariants {
  Default,
  ContactAndCompany,
  Dashboard,
}

type InvoiceTableFilter = {
  field: "contactId" | "companyId" | "quoteId" | "status";
  value: string;
};

interface InvoicesTableProps {
  variant: InvoicesTableVariants;
  filterAdded?: InvoiceTableFilter;
  defaultRowsPerPage?: number;
}

export default function InvoicesTable({
  variant,
  filterAdded,
  defaultRowsPerPage,
}: InvoicesTableProps) {
  const { account } = useAuthContext();
  const { loadingTrigger } = useSharedContext();
  const isMobile = useResponsive("down", "sm");
  const theme = useTheme();

  const {
    page,
    order,
    orderBy,
    rowsPerPage,
    setPage,
    setRowsPerPage,
    //
    selected,
    //
    onSort,
    onChangePage,
    onChangeRowsPerPage,
  } = useTable({ defaultOrderBy: "createDate" });

  const [tableData, setTableData] = useState<dbInvoice[]>([]);

  const [filterName, setFilterName] = useState("");

  const [filterStatus, setFilterStatus] = useState("all");

  const [filterService, setFilterService] = useState("all");

  const [filterEndDate, setFilterEndDate] = useState<Date | null>(null);

  const [filterStartDate, setFilterStartDate] = useState<Date | null>(null);

  const [isLoading, setIsLoading] = useState(true);

  const dataFiltered = applyFilter({
    inputData: tableData,
    comparator: getComparator(order, orderBy),
    filterName,
    filterService,
    filterStatus,
    filterStartDate,
    filterEndDate,
  });

  const dataInPage = dataFiltered.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const isFiltered =
    filterStatus !== "all" ||
    filterName !== "" ||
    filterService !== "all" ||
    (!!filterStartDate && !!filterEndDate);

  const isNotFound =
    (!dataFiltered.length && !!filterName) ||
    (!dataFiltered.length && !!filterStatus) ||
    (!dataFiltered.length && !!filterService) ||
    (!dataFiltered.length && !!filterEndDate) ||
    (!dataFiltered.length && !!filterStartDate);

  const getLengthByStatus = (status: string) =>
    tableData.filter((item) => item.status === status).length;

  const getTotalPriceByStatus = (status: string) =>
    tableData
      .filter((item) => item.status === status)
      .reduce(
        (prev, current) =>
          prev + current.products.reduce((p, c) => p + c.total, 0),
        0
      );

  const getPercentByStatus = (status: string) =>
    (getLengthByStatus(status) / tableData.length) * 100;

  const getLengthByOverdue = () =>
    tableData.filter(
      (item) =>
        item.dueDate < format(new Date(), "yyyy-MM-dd") &&
        item.status !== "Paid"
    ).length;

  const getTotalPriceByOverdue = () =>
    tableData
      .filter((item) => item.dueDate < format(new Date(), "yyyy-MM-dd"))
      .reduce(
        (prev, current) =>
          prev + current.products.reduce((p, c) => p + c.total, 0),
        0
      );

  const getPercentByOverdue = () =>
    (getLengthByOverdue() / tableData.length) * 100;

  const TABS = [
    { value: "all", label: "All", color: "info", count: tableData.length },
    {
      value: "Paid",
      label: "Paid",
      color: "success",
      count: getLengthByStatus("Paid"),
    },
    {
      value: "Sent to customer",
      label: "Sent to customer",
      color: "warning",
      count: getLengthByStatus("Sent to customer"),
    },
    // {
    //   value: "overdue",
    //   label: "Overdue",
    //   color: "error",
    //   count: getLengthByStatus("overdue"),
    // },
    {
      value: "New",
      label: "New",
      color: "default",
      count: getLengthByStatus("New"),
    },
  ] as const;

  const handleFilterStatus = (
    event: React.SyntheticEvent<Element, Event>,
    newValue: string
  ) => {
    setPage(0);
    setFilterStatus(newValue);
  };

  const handleFilterName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const handleResetFilter = () => {
    setFilterName("");
    setFilterStatus("all");
    setFilterService("all");
    setFilterEndDate(null);
    setFilterStartDate(null);
  };

  const handleDeleteRow = async (id: string | undefined) => {
    await axios.post("/api/invoice/disable", {
      invoiceId: id,
    });

    const deleteRow = tableData.filter((row) => row.invoiceId !== id);
    setTableData(deleteRow);

    if (page > 0) {
      if (dataInPage.length < 2) {
        setPage(page - 1);
      }
    }
  };

  useEffect(() => {
    setRowsPerPage(defaultRowsPerPage || 25);

    axiosInstance
      .get(`/api/invoice/list?accountId=${account?.accountId}`)
      .then((response) => {
        if (filterAdded) {
          if (filterAdded.field === "status") {
            if (filterAdded.value === "pending") {
              setTableData(
                response.data.results.filter(
                  (invoice: dbInvoice) => invoice.status !== "Paid"
                )
              );
            }
          } else {
            setTableData(
              response.data.results.filter(
                (invoice: dbInvoice) =>
                  invoice[filterAdded.field] === filterAdded.value
              )
            );
          }
        } else {
          setTableData(response.data.results);
        }
        setIsLoading(false);
      });
  }, [loadingTrigger]);

  return (
    <>
      {variant === InvoicesTableVariants.Default && !isMobile && (
        <Card sx={{ mb: 5 }}>
          <Scrollbar>
            <Stack
              direction="row"
              divider={
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ borderStyle: "dashed" }}
                />
              }
              sx={{ py: 2 }}
            >
              <InvoiceAnalytic
                title="Total"
                elementName="Facturas"
                total={tableData.length}
                percent={100}
                price={tableData.reduce(
                  (prev, current) =>
                    prev + current.products.reduce((p, c) => p + c.total, 0),
                  0
                )}
                icon="ic:round-receipt"
                color={theme.palette.info.main}
              />

              <InvoiceAnalytic
                title="Paid"
                elementName="Facturas"
                total={getLengthByStatus("Paid")}
                percent={getPercentByStatus("Paid")}
                price={getTotalPriceByStatus("Paid")}
                icon="eva:checkmark-circle-2-fill"
                color={theme.palette.success.main}
              />

              <InvoiceAnalytic
                title="Sent"
                elementName="Facturas"
                total={getLengthByStatus("Sent to customer")}
                percent={getPercentByStatus("Sent to customer")}
                price={getTotalPriceByStatus("Sent to customer")}
                icon="eva:clock-fill"
                color={theme.palette.warning.main}
              />

              <InvoiceAnalytic
                title="Overdue"
                elementName="Facturas"
                total={getLengthByOverdue()}
                percent={getPercentByOverdue()}
                price={getTotalPriceByOverdue()}
                icon="eva:bell-fill"
                color={theme.palette.error.main}
              />

              <InvoiceAnalytic
                title="New"
                elementName="Facturas"
                total={getLengthByStatus("New")}
                percent={getPercentByStatus("New")}
                price={getTotalPriceByStatus("New")}
                icon="eva:file-fill"
                color={theme.palette.text.secondary}
              />
            </Stack>
          </Scrollbar>
        </Card>
      )}

      <Card>
        {variant === InvoicesTableVariants.Default && !isMobile && (
          <>
            <Tabs
              value={filterStatus}
              onChange={handleFilterStatus}
              sx={{
                px: 2,
                bgcolor: "background.neutral",
              }}
            >
              {TABS.map((tab) => (
                <Tab
                  key={tab.value}
                  value={tab.value}
                  label={tab.label}
                  icon={
                    <Label color={tab.color} sx={{ mr: 1 }}>
                      {tab.count}
                    </Label>
                  }
                />
              ))}
            </Tabs>

            <Divider />
          </>
        )}

        {variant !== InvoicesTableVariants.Dashboard && (
          <InvoiceTableToolbar
            isFiltered={isFiltered}
            filterName={filterName}
            filterEndDate={filterEndDate}
            onFilterName={handleFilterName}
            onResetFilter={handleResetFilter}
            filterStartDate={filterStartDate}
            onFilterStartDate={(newValue) => {
              setFilterStartDate(newValue);
            }}
            onFilterEndDate={(newValue) => {
              setFilterEndDate(newValue);
            }}
            variant={variant}
          />
        )}

        {!isMobile && (
          <>
            <TableContainer sx={{ position: "relative", overflow: "unset" }}>
              <Scrollbar>
                <Table sx={{ minWidth: 800 }}>
                  <TableHeadCustom
                    //order={order}
                    //orderBy={orderBy}
                    headLabel={TABLE_HEAD}
                    rowCount={tableData.length}
                    numSelected={selected.length}
                    //onSort={onSort}
                  />

                  {isLoading ? (
                    <TableBody>
                      <TableRow>
                        <TableCell colSpan={12}>
                          Cargando información...
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  ) : (
                    <TableBody>
                      {dataFiltered
                        .slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage
                        )
                        .map((row) => (
                          <InvoiceTableRow
                            key={row.invoiceId}
                            row={row}
                            onSelectRow={() =>
                              console.log("onSelectRow(row.id)")
                            }
                            onViewRow={() =>
                              console.log("handleViewRow(row.id)")
                            }
                            onEditRow={() =>
                              console.log("handleEditRow(row.id)")
                            }
                            onDeleteRow={() => handleDeleteRow(row.invoiceId)}
                          />
                        ))}

                      <TableEmptyRows
                        emptyRows={emptyRows(
                          page,
                          rowsPerPage,
                          tableData.length
                        )}
                      />

                      <TableNoData isNotFound={isNotFound} />
                    </TableBody>
                  )}
                </Table>
              </Scrollbar>
            </TableContainer>

            <TablePaginationCustom
              count={dataFiltered.length}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={onChangePage}
              onRowsPerPageChange={onChangeRowsPerPage}
              rowsPerPageOptions={
                defaultRowsPerPage && defaultRowsPerPage !== 25
                  ? [defaultRowsPerPage, 25, 50, 100]
                  : [25, 50, 100]
              }
            />
          </>
        )}
      </Card>
      {isMobile && (
        <>
          {isLoading ? (
            <>Cargando información...</>
          ) : (
            dataFiltered.map((row) => (
              <InvoiceCard key={row.invoiceId} row={row} />
            ))
          )}
        </>
      )}
    </>
  );
}

// ----------------------------------------------------------------------

function applyFilter({
  inputData,
  comparator,
  filterName,
  filterStatus,
  filterService,
  filterStartDate,
  filterEndDate,
}: {
  inputData: dbInvoice[];
  comparator: (a: any, b: any) => number;
  filterName: string;
  filterStatus: string;
  filterService: string;
  filterStartDate: Date | null;
  filterEndDate: Date | null;
}) {
  const stabilizedThis = inputData.map((el, index) => [el, index] as const);

  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  inputData = stabilizedThis.map((el) => el[0]);

  if (filterName) {
    inputData = inputData.filter(
      (invoice) =>
        (invoice.companyName &&
          invoice.companyName
            .toLowerCase()
            .indexOf(filterName.toLowerCase()) !== -1) ||
        (invoice.contactName &&
          invoice.contactName
            .toLowerCase()
            .indexOf(filterName.toLowerCase()) !== -1) ||
        invoice.invoiceId.toLowerCase().indexOf(filterName.toLowerCase()) !== -1
    );
  }

  if (filterStatus !== "all") {
    inputData = inputData.filter((invoice) => invoice.status === filterStatus);
  }

  // if (filterService !== "all") {
  //   inputData = inputData.filter((invoice) =>
  //     invoice.items.some((c) => c.service === filterService)
  //   );
  // }

  if (filterStartDate && filterEndDate) {
    inputData = inputData.filter(
      (invoice) =>
        fTimestamp(invoice.createdDate) >= fTimestamp(filterStartDate) &&
        fTimestamp(invoice.createdDate) <= fTimestamp(filterEndDate)
    );
  }

  return inputData;
}
